import React from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import { post } from '../api/baseUrl';

export type SystemSettingRoles = Record<
  string,
  { name: string; value: string[] }
>;

interface RoleResult {
  permissionKeyValuePair: {
    [key: string]: string;
  };
  tenancy_roles: {
    [key: string]: {
      name: string;
      value: string[];
    };
  };
}

interface RolesContextType {
  isLoading: boolean;
  errorMessage: string;
  tenancyRoles: SystemSettingRoles;
  permissionKeyValuePair: Record<string, string>;
  fetchAllRoles: () => Promise<void>;
}

const RolesContext = React.createContext<RolesContextType>({
  isLoading: false,
  errorMessage: '',
  tenancyRoles: {},
  permissionKeyValuePair: {},
  fetchAllRoles: async () => {},
});

export const useAllRoles = () => React.useContext(RolesContext);

const RolesProvider = ({ children }: { children: React.ReactNode }) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [tenancyRoles, setTenancyRoles] = React.useState<SystemSettingRoles>(
    {},
  );
  const [permissionKeyValuePair, setPermissionKeyValuePair] = React.useState<
    Record<string, string>
  >({});
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );

  const fetchAllRoles = React.useCallback(async () => {
    setIsLoading(true);
    try {
      if (!selectedTenancy) return;
      const payload = {
        tenancyId: selectedTenancy.tenancy_id,
      };
      const {
        data: { data },
      } = await post(`getAllRoles`, { ...payload });

      const { tenancy_roles, permissionKeyValuePair } = data as RoleResult;
      setTenancyRoles({ ...tenancy_roles });
      setPermissionKeyValuePair({ ...permissionKeyValuePair });
    } catch (error) {
      console.error('An error occurred during patch getAllRoles', error);
      setErrorMessage(
        // @ts-ignore
        error?.response?.data?.message || 'Unknown error in patch getAllRoles',
      );
    } finally {
      setIsLoading(false);
    }
  }, [selectedTenancy]);

  const value = React.useMemo(
    () => ({
      isLoading,
      errorMessage,
      tenancyRoles,
      fetchAllRoles,
      permissionKeyValuePair,
    }),
    [
      tenancyRoles,
      fetchAllRoles,
      permissionKeyValuePair,
      isLoading,
      errorMessage,
    ],
  );
  return (
    <RolesContext.Provider value={value}>{children}</RolesContext.Provider>
  );
};

export default RolesProvider;
